import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2, RotateCcw, Sparkles, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import inaraSymbol from '@/assets/greenin-impact-symbol-v2.svg';

type Message = { role: 'user' | 'assistant'; content: string };

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/greenin-chat`;

const SESSION_MESSAGES_KEY = 'inara-chat-messages';
const SESSION_OPEN_KEY = 'inara-chat-open';
const SESSION_TOOLTIP_KEY = 'inara-tooltip-shown';

// Parse and extract suggestions from assistant content
function extractSuggestions(content: string): { cleanContent: string; suggestions: string[] } {
  const match = content.match(/\[SUGGESTIONS\]\s*([\s\S]*?)\s*\[\/SUGGESTIONS\]\s*$/);
  if (!match) return { cleanContent: content, suggestions: [] };

  const cleanContent = content.slice(0, match.index).trimEnd();
  const suggestions = match[1]
    .split('\n')
    .map(line => line.replace(/^[-*]\s*/, '').trim())
    .filter(line => line.length > 0);

  return { cleanContent, suggestions };
}

// Smart link click handler
function createLinkClickHandler(href: string, navigate: ReturnType<typeof useNavigate>, setIsOpen: (v: boolean) => void) {
  return (e: React.MouseEvent) => {
    e.preventDefault();

    const hashRouteMatch = href.match(/^\/#(.+)$/);
    if (hashRouteMatch) {
      const id = hashRouteMatch[1];
      setIsOpen(false);
      setTimeout(() => {
        navigate('/', { replace: false });
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
          }
        }, 150);
      }, 300);
      return;
    }

    if (href.startsWith('#')) {
      const id = href.slice(1);
      const el = document.getElementById(id);
      if (el) {
        setIsOpen(false);
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 300);
      }
    } else if (href.startsWith('/')) {
      setIsOpen(false);
      setTimeout(() => navigate(href), 300);
    } else {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };
}

// Simple markdown renderer (bold, italic, links, lists)
function renderMarkdown(text: string, navigate: ReturnType<typeof useNavigate>, setIsOpen: (v: boolean) => void) {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];

  lines.forEach((line, i) => {
    if (/^[-*]\s/.test(line)) {
      elements.push(
        <li key={i} className="ml-4 list-disc text-sm">
          {renderInline(line.replace(/^[-*]\s/, ''), navigate, setIsOpen)}
        </li>
      );
      return;
    }
    if (/^\d+\.\s/.test(line)) {
      elements.push(
        <li key={i} className="ml-4 list-decimal text-sm">
          {renderInline(line.replace(/^\d+\.\s/, ''), navigate, setIsOpen)}
        </li>
      );
      return;
    }
    if (/^###\s/.test(line)) {
      elements.push(<p key={i} className="font-bold text-sm mt-2">{line.replace(/^###\s/, '')}</p>);
      return;
    }
    if (/^##\s/.test(line)) {
      elements.push(<p key={i} className="font-bold text-base mt-2">{line.replace(/^##\s/, '')}</p>);
      return;
    }
    if (line.trim() === '') {
      elements.push(<br key={i} />);
      return;
    }
    elements.push(<p key={i} className="text-sm">{renderInline(line, navigate, setIsOpen)}</p>);
  });

  return <>{elements}</>;
}

// Allow-list of safe URL schemes/paths for AI-generated links.
// Prevents javascript:, data:, vbscript:, file:, etc. from being rendered.
function isSafeHref(href: string): boolean {
  if (!href || typeof href !== 'string') return false;
  const trimmed = href.trim();
  // Internal paths and fragments are safe
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) return true;
  // Allow-list explicit safe schemes only
  const lower = trimmed.toLowerCase();
  return (
    lower.startsWith('https://') ||
    lower.startsWith('http://') ||
    lower.startsWith('mailto:') ||
    lower.startsWith('tel:')
  );
}

function renderLink(linkText: string, href: string, key: number | string, navigate: ReturnType<typeof useNavigate>, setIsOpen: (v: boolean) => void, bold = false) {
  const content = bold ? <strong>{linkText}</strong> : linkText;
  // If href scheme is not allow-listed, render plain text instead of an anchor.
  if (!isSafeHref(href)) {
    return <span key={key}>{content}</span>;
  }
  const isInternal = href.startsWith('/') || href.startsWith('#');
  return (
    <a
      key={key}
      href={href}
      onClick={createLinkClickHandler(href, navigate, setIsOpen)}
      target={isInternal ? undefined : '_blank'}
      rel={isInternal ? undefined : 'noopener noreferrer'}
      className="text-primary underline hover:text-primary/80 cursor-pointer"
    >
      {content}
    </a>
  );
}

function renderInline(text: string, navigate: ReturnType<typeof useNavigate>, setIsOpen: (v: boolean) => void): React.ReactNode {
  const parts = text.split(/(\*\*\[[^\]]+\]\([^)]+\)\*\*|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const boldLinkMatch = part.match(/^\*\*\[([^\]]+)\]\(([^)]+)\)\*\*$/);
    if (boldLinkMatch) {
      return renderLink(boldLinkMatch[1], boldLinkMatch[2], i, navigate, setIsOpen, true);
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      const inner = part.slice(2, -2);
      const innerLinkMatch = inner.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (innerLinkMatch) {
        return renderLink(innerLinkMatch[1], innerLinkMatch[2], i, navigate, setIsOpen, true);
      }
      return <strong key={i}>{inner}</strong>;
    }
    const linkMatch = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      return renderLink(linkMatch[1], linkMatch[2], i, navigate, setIsOpen);
    }
    return part;
  });
}

/** Inara avatar — reusable symbol component with premium relief */
const InaraAvatar = ({ size = 'sm' }: { size?: 'sm' | 'md' | 'lg' }) => {
  const dims = size === 'lg' ? 'w-10 h-10' : size === 'md' ? 'w-9 h-9' : 'w-8 h-8';
  const imgDims = size === 'lg' ? 'w-7 h-7' : size === 'md' ? 'w-6 h-6' : 'w-5 h-5';
  return (
    <div className={`${dims} rounded-full bg-gradient-to-br from-[#6600B3]/20 to-[#6600B3]/8 ring-1 ring-[#6600B3]/30 shadow-md shadow-[#6600B3]/15 flex items-center justify-center shrink-0 dark:from-[#6600B3]/25 dark:to-[#6600B3]/10 dark:ring-[#6600B3]/35`}>
      <img src={inaraSymbol} alt="Inara AI assistant" className={`${imgDims} drop-shadow-[0_1px_2px_rgba(102,0,179,0.35)]`} />
    </div>
  );
};

const ChatWidget = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_OPEN_KEY) === 'true';
    } catch { return false; }
  });
  const [messages, setMessages] = useState<Message[]>(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_MESSAGES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [followUpSuggestions, setFollowUpSuggestions] = useState<string[]>([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Multi-language UI text helpers
  const uiText: Record<string, Record<string, string>> = {
    en: {
      tooltip: "Hi! I'm Inara, your guide 🌿",
      guideLabel: 'Your Impact Guide · Online',
      newConvo: 'New conversation',
      closeChat: 'Close chat',
      openAssistant: 'Open Inara assistant',
      placeholder: 'Type your question...',
      errorMsg: 'Sorry, something went wrong. Please try again.',
      suggestions: 'Suggestions',
      welcomeMsg: "Hi there! 🌿 I'm **Inara**, your GreenIn Impact guide. I can help you learn about our programs, the GreenIn ecosystem, and how to get involved. I can also connect you with a consultant, our impact team, or a direct WhatsApp channel.",
      askHow: 'How can I help you?',
    },
    es: {
      tooltip: '¡Hola! Soy Inara, tu guía 🌿',
      guideLabel: 'Tu Guía de Impacto · En línea',
      newConvo: 'Nueva conversación',
      closeChat: 'Cerrar chat',
      openAssistant: 'Abrir asistente Inara',
      placeholder: 'Escribe tu pregunta...',
      errorMsg: 'Lo siento, hubo un error. Por favor intenta de nuevo.',
      suggestions: 'Sugerencias',
      welcomeMsg: '¡Hola! 🌿 Soy **Inara**, tu guía de impacto de GreenIn Impact. Puedo ayudarte a conocer nuestros programas, el ecosistema GreenIn y cómo participar. También puedo conectarte con un consultor, nuestro equipo de impacto o un canal directo por WhatsApp.',
      askHow: '¿En qué te puedo ayudar?',
    },
    pt: {
      tooltip: 'Olá! Sou Inara, sua guia 🌿',
      guideLabel: 'Sua Guia de Impacto · Online',
      newConvo: 'Nova conversa',
      closeChat: 'Fechar chat',
      openAssistant: 'Abrir assistente Inara',
      placeholder: 'Digite sua pergunta...',
      errorMsg: 'Desculpe, algo deu errado. Por favor, tente novamente.',
      suggestions: 'Sugestões',
      welcomeMsg: 'Olá! 🌿 Sou a **Inara**, sua guia de impacto do GreenIn Impact. Posso ajudá-lo a conhecer nossos programas, o ecossistema GreenIn e como participar. Também posso conectá-lo com um consultor, nossa equipe de impacto ou um canal direto pelo WhatsApp.',
      askHow: 'Como posso ajudá-lo?',
    },
    fr: {
      tooltip: 'Bonjour ! Je suis Inara, votre guide 🌿',
      guideLabel: 'Votre Guide Impact · En ligne',
      newConvo: 'Nouvelle conversation',
      closeChat: 'Fermer le chat',
      openAssistant: "Ouvrir l'assistant Inara",
      placeholder: 'Écrivez votre question...',
      errorMsg: "Désolé, une erreur s'est produite. Veuillez réessayer.",
      suggestions: 'Suggestions',
      welcomeMsg: "Bonjour ! 🌿 Je suis **Inara**, votre guide d'impact GreenIn. Je peux vous aider à découvrir nos programmes, l'écosystème GreenIn et comment participer. Je peux aussi vous mettre en contact avec un consultant, notre équipe impact ou un canal WhatsApp direct.",
      askHow: 'Comment puis-je vous aider ?',
    },
  };
  const txt = (key: string) => uiText[language]?.[key] || uiText['en'][key];

  const initialSuggestionsData: Record<string, { emoji: string; text: string }[]> = {
    en: [
      { emoji: '🌱', text: 'What is GreenIn Impact?' },
      { emoji: '🤝', text: 'How can I get involved?' },
      { emoji: '📋', text: 'Tell me about the programs' },
      { emoji: '🌐', text: 'What is the GreenIn Ecosystem?' },
      { emoji: '💬', text: 'I want to talk to someone' },
    ],
    es: [
      { emoji: '🌱', text: '¿Qué es GreenIn Impact?' },
      { emoji: '🤝', text: '¿Cómo puedo participar?' },
      { emoji: '📋', text: 'Cuéntame sobre los programas' },
      { emoji: '🌐', text: '¿Qué es el Ecosistema GreenIn?' },
      { emoji: '💬', text: 'Quiero hablar con alguien' },
    ],
    pt: [
      { emoji: '🌱', text: 'O que é o GreenIn Impact?' },
      { emoji: '🤝', text: 'Como posso participar?' },
      { emoji: '📋', text: 'Conte-me sobre os programas' },
      { emoji: '🌐', text: 'O que é o Ecossistema GreenIn?' },
      { emoji: '💬', text: 'Quero falar com alguém' },
    ],
    fr: [
      { emoji: '🌱', text: "Qu'est-ce que GreenIn Impact ?" },
      { emoji: '🤝', text: 'Comment puis-je participer ?' },
      { emoji: '📋', text: 'Parlez-moi des programmes' },
      { emoji: '🌐', text: "Qu'est-ce que l'Écosystème GreenIn ?" },
      { emoji: '💬', text: "Je veux parler à quelqu'un" },
    ],
  };
  const initialSuggestions = initialSuggestionsData[language] || initialSuggestionsData['en'];

  // Animated tooltip — first visit only
  useEffect(() => {
    if (isOpen) return;
    try {
      if (sessionStorage.getItem(SESSION_TOOLTIP_KEY)) return;
    } catch { return; }

    const showTimer = setTimeout(() => setShowTooltip(true), 2000);
    const hideTimer = setTimeout(() => {
      setShowTooltip(false);
      try { sessionStorage.setItem(SESSION_TOOLTIP_KEY, '1'); } catch {}
    }, 7000);

    return () => { clearTimeout(showTimer); clearTimeout(hideTimer); };
  }, [isOpen]);

  // Hide tooltip when chat opens
  useEffect(() => {
    if (isOpen && showTooltip) {
      setShowTooltip(false);
      try { sessionStorage.setItem(SESSION_TOOLTIP_KEY, '1'); } catch {}
    }
  }, [isOpen, showTooltip]);

  // Persist messages to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_MESSAGES_KEY, JSON.stringify(messages));
    } catch { /* ignore */ }
  }, [messages]);

  // Persist isOpen state
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_OPEN_KEY, String(isOpen));
    } catch { /* ignore */ }
  }, [isOpen]);

  // Extract suggestions from last assistant message on load
  useEffect(() => {
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.role === 'assistant') {
        const { suggestions } = extractSuggestions(lastMsg.content);
        if (suggestions.length > 0) {
          setFollowUpSuggestions(suggestions);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, followUpSuggestions, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const trackSuggestion = useCallback((text: string, type: 'initial' | 'follow_up') => {
    supabase.from('ivy_analytics').insert({
      suggestion_text: text,
      suggestion_type: type,
      language,
    }).then(() => {});
  }, [language]);

  const sendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return;

    setFollowUpSuggestions([]);

    const userMsg: Message = { role: 'user', content: content.trim() };
    const allMessages = [...messages, userMsg];
    setMessages(allMessages);
    setInput('');
    setIsLoading(true);

    let assistantSoFar = '';

    try {
      const resp = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ messages: allMessages, language }),
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.error || `Error ${resp.status}`);
      }

      if (!resp.body) throw new Error('No response body');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = '';
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const delta = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (delta) {
              assistantSoFar += delta;
              const currentContent = assistantSoFar;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant') {
                  return prev.map((m, i) =>
                    i === prev.length - 1 ? { ...m, content: currentContent } : m
                  );
                }
                return [...prev, { role: 'assistant', content: currentContent }];
              });
            }
          } catch {
            textBuffer = line + '\n' + textBuffer;
            break;
          }
        }
      }

      // Flush remaining
      if (textBuffer.trim()) {
        for (let raw of textBuffer.split('\n')) {
          if (!raw) continue;
          if (raw.endsWith('\r')) raw = raw.slice(0, -1);
          if (raw.startsWith(':') || raw.trim() === '') continue;
          if (!raw.startsWith('data: ')) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === '[DONE]') continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const delta = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (delta) {
              assistantSoFar += delta;
              const currentContent = assistantSoFar;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant') {
                  return prev.map((m, i) =>
                    i === prev.length - 1 ? { ...m, content: currentContent } : m
                  );
                }
                return [...prev, { role: 'assistant', content: currentContent }];
              });
            }
          } catch { /* ignore */ }
        }
      }

      // Extract suggestions from completed response
      const { cleanContent, suggestions } = extractSuggestions(assistantSoFar);
      if (suggestions.length > 0) {
        setFollowUpSuggestions(suggestions);
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant') {
            return prev.map((m, i) =>
              i === prev.length - 1 ? { ...m, content: cleanContent } : m
            );
          }
          return prev;
        });
      }
    } catch (err) {
      console.error('Inara chat error:', err);
      setMessages(prev => [...prev, { role: 'assistant', content: txt('errorMsg') }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const getDisplayContent = (msg: Message, index: number) => {
    if (msg.role !== 'assistant') return msg.content;
    const { cleanContent } = extractSuggestions(msg.content);
    return cleanContent;
  };

  return (
    <>
      {/* Floating button + tooltip */}
      <AnimatePresence>
        {!isOpen && (
          <div className="fixed bottom-36 right-6 z-50 flex items-center gap-3 md:bottom-20">
            {/* Animated tooltip pill */}
            <AnimatePresence>
              {showTooltip && (
                <motion.div
                  initial={{ opacity: 0, x: 20, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 10, scale: 0.95 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  className="bg-card border border-border/60 text-foreground text-xs font-medium px-4 py-2.5 rounded-2xl shadow-xl whitespace-nowrap backdrop-blur-sm"
                >
                  <span>{txt('tooltip')}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.button
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              onClick={() => setIsOpen(true)}
              className="group relative w-14 h-14 rounded-full bg-gradient-to-br from-[#6600B3] to-[#5500A0] text-inara-foreground shadow-lg shadow-[#6600B3]/30 hover:shadow-xl hover:shadow-[#6600B3]/45 ring-1 ring-white/15 flex items-center justify-center transition-all duration-300 hover:scale-105"
              aria-label={txt('openAssistant')}
            >
              {/* White inner circle for symbol contrast */}
              <div className="w-10 h-10 rounded-full bg-white/95 flex items-center justify-center shadow-inner group-hover:bg-white transition-colors duration-300">
                <img src={inaraSymbol} alt="Inara AI assistant" className="w-8 h-8 drop-shadow-[0_1px_2px_rgba(102,0,179,0.4)] group-hover:scale-110 transition-transform duration-300" />
              </div>
              {/* Subtle pulse ring */}
              <span className="absolute inset-0 rounded-full animate-ping opacity-[0.08] bg-white pointer-events-none" />
            </motion.button>
          </div>
        )}
      </AnimatePresence>

      {/* Chat window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-4 right-4 z-50 w-[calc(100vw-2rem)] sm:w-[400px] h-[min(600px,calc(100vh-2rem))] flex flex-col rounded-2xl border border-border/80 bg-card shadow-2xl shadow-black/20 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#6600B3] to-[#5500A0] text-inara-foreground shrink-0 border-b border-white/5">
              <div className="flex items-center gap-2.5">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-white/25 ring-1 ring-white/15 shadow-inner backdrop-blur-sm flex items-center justify-center">
                    <img src={inaraSymbol} alt="Inara AI assistant" className="w-7 h-7 drop-shadow-[0_1px_3px_rgba(255,255,255,0.5)]" />
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-inara-foreground border-2 border-inara" />
                </div>
                <div>
                  <p className="font-bold text-sm leading-tight tracking-wide">Inara</p>
                  <p className="text-[11px] opacity-75">
                    {txt('guideLabel')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 0 && (
                  <button
                    onClick={() => {
                      setMessages([]);
                      setFollowUpSuggestions([]);
                      setInput('');
                      try { sessionStorage.removeItem(SESSION_MESSAGES_KEY); } catch {}
                    }}
                    className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
                    aria-label={txt('newConvo')}
                    title={txt('newConvo')}
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
                  aria-label={txt('closeChat')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {/* Welcome message */}
              {messages.length === 0 && (
                <div className="space-y-4">
                  {/* Avatar + greeting */}
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5">
                      <InaraAvatar size="md" />
                    </div>
                    <div className="bg-muted/50 rounded-2xl rounded-tl-md p-3.5 max-w-[85%]">
                      <p className="text-sm text-foreground leading-relaxed">
                        {renderInline(txt('welcomeMsg'), navigate, setIsOpen)}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        {txt('askHow')}
                      </p>
                    </div>
                  </div>

                  {/* Initial suggestion chips — vertical cards */}
                  <div className="grid gap-2 pl-9">
                    {initialSuggestions.map((s, i) => (
                      <motion.button
                        key={i}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 + i * 0.06, duration: 0.25 }}
                        onClick={() => { trackSuggestion(s.text, 'initial'); sendMessage(s.text); }}
                        className="group flex items-center gap-2.5 text-left text-sm px-3.5 py-2.5 rounded-xl border border-border/60 bg-card hover:border-inara/40 hover:bg-inara/5 transition-all duration-200"
                      >
                        <span className="text-base shrink-0">{s.emoji}</span>
                        <span className="flex-1 text-foreground/80 group-hover:text-foreground transition-colors">{s.text}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-inara transition-colors shrink-0" />
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="mt-1 mr-2">
                      <InaraAvatar size="sm" />
                    </div>
                  )}
                  <div
                    className={`rounded-2xl px-3.5 py-2.5 max-w-[80%] ${
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground rounded-br-md'
                        : 'bg-muted/50 text-foreground rounded-tl-md'
                    }`}
                  >
                    {msg.role === 'assistant'
                      ? renderMarkdown(getDisplayContent(msg, i), navigate, setIsOpen)
                      : <p className="text-sm">{msg.content}</p>}
                  </div>
                </motion.div>
              ))}

              {/* Follow-up suggestions */}
              {!isLoading && followUpSuggestions.length > 0 && (
                <motion.div 
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-1.5 pl-8 pt-1"
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <Sparkles className="w-3 h-3 text-inara/60" />
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                      {txt('suggestions')}
                    </span>
                  </div>
                  {followUpSuggestions.map((s, i) => (
                    <motion.button
                      key={i}
                      initial={{ opacity: 0, x: -4 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05, duration: 0.2 }}
                      onClick={() => { trackSuggestion(s, 'follow_up'); sendMessage(s); }}
                      className="group flex items-center gap-2 w-full text-left text-xs px-3 py-2 rounded-lg border border-border/50 bg-card hover:border-inara/30 hover:bg-inara/5 transition-all duration-200"
                    >
                      <span className="flex-1 text-foreground/70 group-hover:text-foreground transition-colors">{s}</span>
                      <ArrowRight className="w-3 h-3 text-muted-foreground/40 group-hover:text-inara transition-colors shrink-0" />
                    </motion.button>
                  ))}
                </motion.div>
              )}

              {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
                <div className="flex items-start gap-2">
                  <InaraAvatar size="sm" />
                  <div className="bg-muted/50 rounded-2xl rounded-tl-md px-4 py-3">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:0ms]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:150ms]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:300ms]" />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-3 border-t border-border/50 shrink-0 bg-card">
              <div className="flex gap-2 items-center">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={txt('placeholder')}
                  disabled={isLoading}
                  className="flex-1 text-sm bg-muted/30 border border-border/60 rounded-xl px-4 py-2.5 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-inara/40 focus:border-inara/40 disabled:opacity-50 transition-all"
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={!input.trim() || isLoading}
                  className="rounded-xl w-10 h-10 shrink-0 shadow-sm bg-inara hover:bg-inara/90 text-inara-foreground"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground text-center mt-2 tracking-wide">
                Powered by GreenIn AI · GreenIn Company
              </p>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
